import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import React, { useRef, useState } from 'react';
import { usePopper } from 'react-popper';
import PropTypes from '../../../node_modules/prop-types/index.js';
import { SIDES } from '../constants.js';
import Icon from '../Icon/Icon.js';
import i18n from '../../lang/i18n/index.js';
import { rem } from '../../utils/functions.js';
import safeCustomStyle from '../../utils/safeCustomStyle.js';
import { TOOLTIP_VARIANT } from './constants.js';
import DefaultIcon from './DefaultIcon.js';
import { Root, ToggleButton, TooltipWrapper, Arrow, TooltipContent, TooltipText, CloseButton } from './styledComponents.js';
import useTooltipState from './useTooltipState.js';
import { createPopperConfig } from './utils.js';
import useDerivedState from '../../hooks/useDerivedState.js';
import useId from '../../hooks/useId.js';
import useLang from '../../hooks/useLang.js';
import useTheme from '../../hooks/useTheme.js';
import useOnClickOutside from '../../hooks/useOnClickOutside.js';
import useKeyPress from '../../hooks/useKeyPress.js';

/**
 * Tooltips may be summoned either on hover/focus (desktop) or by click/touch (mobile). Users must tap
elsewhere on their screen, or on the tooltip icon to close the tooltip. If using their keyboard they can press
the ESC key to close the tooltip.

When a user tabs to the tooltip, the screen
reader should read it aloud.

Aria-describedby accessibility violation is a false positive since the Tooltip wrapper will exist when the Tooltip
is focused or hovered.
 */
var Tooltip = function Tooltip(_ref) {
  var _attributes$popper;
  var controlledId = _ref.id,
    side = _ref.side,
    children = _ref.children,
    ariaLabel = _ref.ariaLabel,
    disableAriaForPopup = _ref.disableAriaForPopup,
    title = _ref.title,
    content = _ref.content,
    customStyle = _ref.customStyle,
    bubbleWidth = _ref.bubbleWidth,
    activateOnHover = _ref.hover,
    variant = _ref.variant;
  var _useDerivedState = useDerivedState(useId('tooltip-id-'), controlledId),
    _useDerivedState2 = _slicedToArray(_useDerivedState, 1),
    id = _useDerivedState2[0];
  var lang = useLang();
  var theme = useTheme();
  // need a DOM ref for screen positioning
  var wrapperRef = useRef();
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    toggleNode = _useState2[0],
    setToggleNode = _useState2[1];
  var _useState3 = useState(),
    _useState4 = _slicedToArray(_useState3, 2),
    tooltipNode = _useState4[0],
    setTooltipNode = _useState4[1];
  var _useState5 = useState(),
    _useState6 = _slicedToArray(_useState5, 2),
    arrowNode = _useState6[0],
    setArrowNode = _useState6[1];
  var _usePopper = usePopper(toggleNode, tooltipNode, createPopperConfig(toggleNode, arrowNode, side, bubbleWidth)),
    styles = _usePopper.styles,
    attributes = _usePopper.attributes;
  var popperPlacement = attributes === null || attributes === void 0 ? void 0 : (_attributes$popper = attributes.popper) === null || _attributes$popper === void 0 ? void 0 : _attributes$popper['data-popper-placement'];
  var _useTooltipState = useTooltipState(activateOnHover),
    isHovered = _useTooltipState.isHovered,
    showTooltip = _useTooltipState.showTooltip,
    onHoverStart = _useTooltipState.onHoverStart,
    onHoverEnd = _useTooltipState.onHoverEnd,
    onFocusStart = _useTooltipState.onFocusStart,
    onFocusEnd = _useTooltipState.onFocusEnd,
    onClickToggle = _useTooltipState.onClickToggle,
    onClickClose = _useTooltipState.onClickClose,
    onClickOutside = _useTooltipState.onClickOutside,
    onClickTooltip = _useTooltipState.onClickTooltip,
    onPressEscape = _useTooltipState.onPressEscape,
    _onTouchEnd = _useTooltipState.onTouchEnd;
  useOnClickOutside(wrapperRef, onClickOutside);
  var escPressed = useKeyPress('Escape');
  if (showTooltip && escPressed) {
    onPressEscape();
  }
  var shownContent = content || title;
  var _safeCustomStyle = safeCustomStyle(customStyle),
    rootStyle = _safeCustomStyle.rootStyle,
    toggleButtonStyle = _safeCustomStyle.toggleButtonStyle;
  return /*#__PURE__*/React.createElement(Root, {
    onBlur: onFocusEnd,
    onMouseOut: onHoverEnd,
    rootStyle: rootStyle,
    ref: wrapperRef
  }, /*#__PURE__*/React.createElement(ToggleButton, {
    id: "".concat(id, "-toggle"),
    ref: setToggleNode,
    toggleButtonStyle: toggleButtonStyle,
    onFocus: onFocusStart,
    onMouseOver: onHoverStart,
    onClick: onClickToggle,
    onTouchEnd: function onTouchEnd(event) {
      event.preventDefault();
      _onTouchEnd();
      toggleNode.focus();
    },
    "aria-label": ariaLabel || i18n[lang].tooltip.ariaLabelDefault,
    "aria-describedby": disableAriaForPopup ? undefined : "".concat(id, "-wrapper"),
    "aria-expanded": disableAriaForPopup ? null : showTooltip,
    showTooltip: showTooltip,
    activateOnHover: activateOnHover,
    type: "button",
    theme: theme,
    "data-testid": "toggle-test"
  }, children || /*#__PURE__*/React.createElement(DefaultIcon, {
    id: "".concat(id, "-icon"),
    variant: variant,
    showTooltip: showTooltip || isHovered,
    theme: theme,
    "data-testid": "icon-test"
  })), showTooltip && /*#__PURE__*/React.createElement(TooltipWrapper, _extends({
    role: "tooltip",
    id: "".concat(id, "-wrapper"),
    "data-testid": "wrapper",
    onClick: onClickTooltip,
    "aria-hidden": disableAriaForPopup,
    ref: setTooltipNode,
    style: styles.popper,
    theme: theme,
    bubbleWidth: bubbleWidth
  }, attributes.popper), /*#__PURE__*/React.createElement(Arrow, {
    "data-testid": "arrow",
    bubbleWidth: bubbleWidth,
    theme: theme,
    ref: setArrowNode,
    style: styles.arrow,
    popperPlacement: popperPlacement
  }), /*#__PURE__*/React.createElement(TooltipContent, {
    theme: theme
  }, /*#__PURE__*/React.createElement(TooltipText, null, shownContent), !activateOnHover && /*#__PURE__*/React.createElement(CloseButton, {
    "aria-label": i18n[lang].tooltip.close,
    onClick: function onClick() {
      onClickClose();
      toggleNode.focus();
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    manulifeName: "close",
    fill: theme.colorPalette.neutralColors.light.shade_white,
    color: theme.colorPalette.neutralColors.light.shade_white,
    alt: "close",
    height: rem(12),
    width: rem(12)
  })))));
};
process.env.NODE_ENV !== "production" ? Tooltip.propTypes = {
  /** DOM element ID */
  id: PropTypes.string,
  /**
   * Element to wrap with tooltip, will render default tooltip icon if no children provided
   */
  children: PropTypes.node,
  /**
   * Position of the nub.
   * Not specifying the side means the tooltip will determine side automatically using best placement for a 3x3 grid.
   * Specifying a nub position should only be done to override positioning behaviour.
   */
  side: PropTypes.oneOf([SIDES.BOTTOM, SIDES.BOTTOM_LEFT, SIDES.BOTTOM_RIGHT, SIDES.TOP, SIDES.TOP_LEFT, SIDES.TOP_RIGHT, SIDES.RIGHT // Connected DS only
  ]),

  /** aria-label for tooltip */
  ariaLabel: PropTypes.string,
  /** tooltip hover text, if content prop is provided it will override title */
  title: PropTypes.string,
  /** tooltip text for multi object items ie. two different blocks of text.
   content will override title text */
  content: PropTypes.node,
  /** Exact width of tooltip bubble (necessary for calculating positioning) */
  bubbleWidth: PropTypes.number,
  /**
   * flag to determine if tooltip will have hover functionality for displaying message.
   * Setting hover to false also shows close icon on top right.
   * */
  hover: PropTypes.bool,
  /** Which variant to use if using default icon */
  variant: PropTypes.oneOf([TOOLTIP_VARIANT.PRIMARY, TOOLTIP_VARIANT.SECONDARY]),
  /** Style overrides */
  customStyle: PropTypes.shape({
    rootStyle: PropTypes.object,
    toggleButtonStyle: PropTypes.object
  }),
  /** Should screen readers ignore the tooltip popup? (used internally by ProgressBar) */
  disableAriaForPopup: PropTypes.bool
} : void 0;
Tooltip.defaultProps = {
  id: undefined,
  children: undefined,
  side: null,
  ariaLabel: undefined,
  title: '',
  content: undefined,
  bubbleWidth: 300,
  hover: true,
  variant: TOOLTIP_VARIANT.PRIMARY,
  customStyle: {
    rootStyle: {},
    toggleButtonStyle: {}
  },
  disableAriaForPopup: false
};
process.env.NODE_ENV !== "production" ? Tooltip.displayName = 'Tooltip' : void 0;
var Tooltip$1 = Tooltip;

export { Tooltip$1 as default };
